import { FormControl, Validators } from '@angular/forms';
import { Inject, Injectable } from '@angular/core';
import { ProcessTypeEnum } from '../../models/cartDetails/process-type.enum';
import OtpAuthService from './otp-auth.service';
import { ServiceTypeEnum } from '../../models/cartDetails/service-type.enum';
import { LeadsSupportService } from './leads-support.service';
import SlotStateService from '../../cart-process/slot-state.service';
import StockValidationService from '../../cart-process/stock-validation.service';
import { CartDetailsSourceIdsEnum } from '../../models/cartDetails/data-source-ids.enum';
import { DataLayerService } from '../../customer-actions/data-layer.service';
import { AppInjector } from '../../utils/services/set-app-injector';
import { IMnpOperatorListResponse } from '../../interfaces/mnp-operator-list.interface';
import { CommonModalService } from '../../utils/modals/common-modal.service';
import { NotificationProviderService } from '../../external-interfaces/notification-provider.service';
import { CommonModalTemplatesEnum } from '../../utils/modals/common-modal-templates.enum';
import { CommonInfoService } from '../../cart-process/common-info.service';


@Injectable()
export default class MnpAuthService {

  private changeMarker: string;
  private bundleNumber: number;
  private msisdn: string;
  private resolve: Function;
  private reject: Function;
  private dataLayerService: DataLayerService = AppInjector.get(DataLayerService);

  constructor(
    private otpAuthService: OtpAuthService,
    private leadsSupportService: LeadsSupportService,
    private slotStateService: SlotStateService,
    private stockValidationService: StockValidationService,
    private commonModalService: CommonModalService,
    private notificationProvider: NotificationProviderService,
    private commonInfoService: CommonInfoService,
    @Inject('Endpoint') private endpoint: any,
  ) {
  }

  public startProcess(bundleNumber: number, msisdn: string): Promise<any> {

    const promise = new Promise((resolve: Function, reject: Function) => {
      this.resolve = resolve;
      this.reject = reject;
    });

    this.bundleNumber = bundleNumber;
    this.msisdn = msisdn;
    this.otpAuthService.startProcess(bundleNumber, msisdn, ProcessTypeEnum.MNP)
      .then(
        this.handleAuthSuccess.bind(this),
        this.handleAuthFailure.bind(this)
      );

    return promise;
  }

  private handleAuthSuccess(changeMarker: any): void {
    this.changeMarker = changeMarker;
    this.dataLayerService.generateToken(CartDetailsSourceIdsEnum.CHANGE_PROCESS);
    this.dataLayerService.onChangeProcess();

    this.endpoint('mnp.operatorList', {
      changeMarker: this.changeMarker
    }).run()
      .then(
        this.handleOperatorsListResponse.bind(this)
      )
      .catch((error) => {
        this.handleFailure(error?.body?.errorCode);
      });
  }

  private handleAuthFailure(errorMessage: string): void {
    this.reject(errorMessage);
  }

  private handleFailure(errorCode: string): void {
    if (errorCode &&
        errorCode === 'VERIFY_MSISDN_IN_PLAY' ||
        errorCode === 'VERIFY_MSISDN_IN_RBM' ||
        errorCode === 'VERIFY_MNP_NOT_ALLOWED'
    ) {
      if (this.commonInfoService.isVirginBrand && !this.msisdn) {
        this.leadsSupportService.sendContactVirginModal();
      } else {
        this.leadsSupportService.startLeads({msisdn: this.msisdn, rejectPromise: this.reject});
     }
    } else if (this.commonInfoService.isVirginBrand) {
      this.leadsSupportService.sendContactVirginModal();
    } else {
      this.otpAuthService.showFailureModal();
    }

    this.reject();
  }

  private handleOperatorsListResponse(operatorsListResponse: IMnpOperatorListResponse): void {
    this.showOperatorsListModal(operatorsListResponse);
  }

  private showOperatorsListModal(operatorsData: IMnpOperatorListResponse): void {
    const modalData: any = this.commonModalService.createModalData(CommonModalTemplatesEnum.NUMBER_VERIFICATION);
    const numberVerification = new FormControl('', [Validators.required, Validators.maxLength(6), Validators.minLength(6)]);

    const selectedOperator = operatorsData.operatorList
      .find((operator: any) => operator.code === operatorsData.operator);

    const model = {
      operator: selectedOperator,
      serviceType: selectedOperator.serviceTypeMappings[0],
      operatorList: operatorsData.operatorList,
      contractEnd: '1',
      isVirginBrand: this.commonInfoService.isVirginBrand
    };

    modalData.model = model;
    modalData.numberVerification = numberVerification;

    modalData.operatorChange = () => {
      model.serviceType = model.operator.serviceTypeMappings[0];
    };

    modalData.showServiceTypeSelect = () => modalData.model.operator.serviceTypeMappings.length >= 1;

    const isFormReadyToSend = () => model.contractEnd || model.serviceType.type === ServiceTypeEnum.PREPAID;


    modalData.showContractEndSection = () => modalData.model.serviceType.type !== ServiceTypeEnum.PREPAID;

    modalData.confirmVerification = ($event: MouseEvent) => {
      $event.preventDefault();

      if (isFormReadyToSend()) {
        modalData.isLoaderVisible = true;

        const requestData = {
          changeMarker: this.changeMarker,
          bundleNo: this.bundleNumber,
          msisdn: this.msisdn,
          mnpOperator: model.operator.longName,
          mnpOperatorId: model.operator.code,
          mnpServiceType: model.serviceType.type,
          mnpDate: modalData.showContractEndSection() ? model.contractEnd : null
        };

        this.endpoint('mnp.save', requestData)
        .run()
        .then(() => {
            this.handleConfirmationResponse();
          }
        )
        .catch(() => {
            this.otpAuthService.showFailureModal();
          }
        );
      }
    };

    this.commonModalService.setData(modalData, this.reject);
  }

  private handleConfirmationResponse(): void {
    this.slotStateService.dropTempState(this.bundleNumber);
    this.stockValidationService.invalidateStockForBundleNumber(this.bundleNumber);
    this.commonModalService.removeModal(CommonModalTemplatesEnum.NUMBER_VERIFICATION);

    this.resolve();
  }
}
